import { CountryCode, isSupportedCountry, parsePhoneNumberFromString, PhoneNumber } from 'libphonenumber-js';

export type PhoneNumberCountryStatus = 'allowed' | 'foreign' | 'invalid';

/** Helpers for phone numbers used by communication features. */
export class PhoneNumberHelper {
  /**
   * Returns supported countries configured for phone delivery.
   * Missing, empty or unsupported values fall back to Poland.
   * @param {string[] | null | undefined} countries
   * @return {CountryCode[]}
   */
  public static getAllowedCountries(countries: readonly string[] | null | undefined): CountryCode[] {
    const allowedCountries: CountryCode[] = [];
    countries?.forEach((country) => {
      if (isSupportedCountry(country) && !allowedCountries.includes(country)) {
        allowedCountries.push(country);
      }
    });
    return allowedCountries.length > 0 ? allowedCountries : ['PL'];
  }

  /**
   * Converts a phone number to E.164.
   * @param {string}      number
   * @param {CountryCode} defaultCountry country used for a national number
   * @return {string} E.164 number, or an empty string for unsupported input
   */
  public static fixPhoneNumber(number: string, defaultCountry: CountryCode = 'PL'): string {
    const parsed = PhoneNumberHelper.parseNumber(number, defaultCountry);
    return parsed?.isValid() ? parsed.number : '';
  }

  /**
   * Converts a phone number to E.164 only when its country is allowed.
   * The first country is used for numbers without an international prefix.
   * @param {string}        number
   * @param {CountryCode[]} allowedCountries
   * @return {string} E.164 number, or an empty string when it cannot be sent
   */
  public static fixPhoneNumberForCountries(number: string, allowedCountries: readonly CountryCode[]): string {
    const defaultCountry = allowedCountries[0];
    if (!defaultCountry) return '';

    const parsed = PhoneNumberHelper.parseNumber(number, defaultCountry);
    return parsed?.isValid() && parsed.country && allowedCountries.includes(parsed.country) ? parsed.number : '';
  }

  /**
   * Classifies a phone number against all allowed countries.
   * The first country is used for numbers without an international prefix.
   * @param {string}        number
   * @param {CountryCode[]} countries
   * @return {PhoneNumberCountryStatus}
   */
  public static statusForCountries(number: string, countries: readonly CountryCode[]): PhoneNumberCountryStatus {
    const defaultCountry = countries[0];
    if (!defaultCountry) return 'invalid';

    const parsed = PhoneNumberHelper.parseNumber(number, defaultCountry);
    if (!parsed?.isValid()) return 'invalid';
    return parsed.country && countries.includes(parsed.country) ? 'allowed' : 'foreign';
  }

  /**
   * Parses supported national and international phone number formats.
   * @param {string}      number
   * @param {CountryCode} defaultCountry
   * @return {PhoneNumber | undefined}
   */
  private static parseNumber(number: string, defaultCountry: CountryCode): PhoneNumber | undefined {
    let phoneToParse = number.replace(/[\s().-]/g, '');
    if (!phoneToParse || !/^(?:\+|00)?\d+$/.test(phoneToParse)) return undefined;
    if (phoneToParse.startsWith('00')) phoneToParse = `+${phoneToParse.substring(2)}`;

    // A Polish country prefix without "+" has eleven digits. A national
    // nine-digit number may legitimately start with 48.
    if (defaultCountry === 'PL' && /^48\d{9}$/.test(phoneToParse)) {
      phoneToParse = `+${phoneToParse}`;
    }
    return parsePhoneNumberFromString(phoneToParse, defaultCountry);
  }
}
